

import Vue from 'vue'
import Component from "vue-class-component";
import {Prop} from "vue-property-decorator";
import {namespace} from "vuex-class";
import {IService, IServiceTag} from "@/model/Service/serviceInterfaces";
import {IServiceProvider} from "@/model/Service/serviceProviderInterfaces";
import {IOnOffHandlers} from "bonomo/dist";
import {ITranslatedField} from "@/helpers/TranslationHelper";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import {EUserPermissions} from "@/model/EUserPermissions";
import {getFileImage} from "@/helpers/fileHelpers";
import V2SPImageInput from "@/components/serviceProvider/v2/SpImageInput/V2SPImageInput.vue";
import TranslatableTabs from "@/components/shared/TranslatableTabs.vue";

const serviceProviderServiceModule = namespace("serviceProviderServiceModule");
const serviceProviderModule = namespace("serviceProviderModule");

enum IMAGE_ENDPOINTS {
  logoImage = 'logoImage',
  secondImage = 'secondImage',
}

interface ServiceForm {
  name: ITranslatedField;
  short_description: ITranslatedField;
  description: ITranslatedField;
  price: string;
  sale_price: string;
  on_sale: boolean;
  active: boolean;
  orderby: string;
  mandatory: boolean;
  minimum_locker_cell_size_id: number | null;
  turnaround_time: string;
  tickets: string;
  service_tag_ids: (IServiceTag | string)[];
}

@Component({
  components: {
    V2SPImageInput,
    TranslatableTabs,
  }
})
export default class ServiceEdit extends Vue {

  @Prop()
  protected spId!: number;
  @Prop()
  protected serviceId!: number;

  @serviceProviderServiceModule.Action('fetchService')
  protected fetchService!: (data: { spId: number, serviceId: number }) => any;
  @serviceProviderServiceModule.Action('createService')
  protected createService!: (data: { spId: number, service: Partial<IService> }) => any;
  @serviceProviderServiceModule.Action('updateService')
  protected updateService!: (data: { spId: number, serviceId: number, service: Partial<IService> }) => any;
  @serviceProviderServiceModule.Action('deleteService')
  protected deleteService!: (data: { spId: number, serviceId: number }) => any;
  @serviceProviderServiceModule.Action('fetchTags')
  protected fetchTags!: () => any;
  @serviceProviderServiceModule.Getter('service')
  protected service!: IService;
  @serviceProviderServiceModule.Getter('tags')
  protected availableTags!: IServiceTag[];

  @serviceProviderModule.Action('fetchServiceProvider')
  protected fetchServiceProvider!: (spId: number) => any;
  @serviceProviderModule.Getter('serviceProvider')
  protected serviceProvider!: IServiceProvider;

  protected requiredRule = [(v: string) => !!v || this.$t('common.required')];

  protected loading: boolean = true;
  protected saving: boolean = false;
  protected deleting: boolean = false;
  protected valid: boolean = false;
  protected showDeleteDialog: boolean = false;
  protected ons: IOnOffHandlers = {};

  protected tagSearch: string = '';

  // Image state — same pattern as ServiceProviderCreate.vue
  protected addressImageLogo: string | null = null;
  protected addressImageSecond: string | null = null;
  protected fileImageLogo: File | null = null;
  protected fileImageSecond: File | null = null;
  protected existingLogoUrl: string = '';
  protected existingSecondUrl: string = '';

  protected languages: string[] = ['es-ES', 'ca-ES', 'en-GB', 'fr-FR', 'pt-PT', 'dk-DK', 'de-DE'];
  protected defaultLanguage: string = 'es-ES';

  protected form: ServiceForm = {
    name: {},
    short_description: {},
    description: {},
    price: '0',
    sale_price: '0',
    on_sale: false,
    active: true,
    orderby: '0',
    mandatory: false,
    minimum_locker_cell_size_id: null,
    turnaround_time: '0',
    tickets: '1',
    service_tag_ids: [],
  };

  protected get invalidFields(): string[] {
    return this.languages.filter(lang => !this.form.name[lang]);
  }

  protected get isCreate(): boolean {
    return !this.serviceId;
  }

  protected get canDelete(): boolean {
    const userData = this.$store.getters['authModule/userData'];
    return !this.isCreate && userData &&
        PermissionHelper.hasPermission(userData, EUserPermissions.PERMISSION_DEVELOPER, 0);
  }


  protected get cellSizeOptions() {
    return [
      { text: this.$t('lockers.cells.sizeNames.1'), value: 1 },
      { text: this.$t('lockers.cells.sizeNames.2'), value: 2 },
      { text: this.$t('lockers.cells.sizeNames.3'), value: 3 },
      { text: this.$t('lockers.cells.sizeNames.4'), value: 4 },
      { text: this.$t('lockers.cells.sizeNames.5'), value: 5 },
    ];
  }

  created() {
    this.ons = this.$ons({
      'backend.call.OK.serviceLoaded': this.onServiceLoaded,
      'backend.call.OK.serviceCreated': this.onServiceSaved,
      'backend.call.OK.serviceUpdated': this.onServiceSaved,
      'backend.call.OK.serviceDeleted': this.onServiceDeleted,
      'backend.call.KO.serviceCreated': this.onSaveError,
      'backend.call.KO.serviceUpdated': this.onSaveError,
      'backend.call.KO.serviceDeleted': this.onSaveError,
    });

    this.fetchServiceProvider(this.spId);
    this.fetchTags();

    if (!this.isCreate) {
      this.fetchService({ spId: this.spId, serviceId: this.serviceId });
    } else {
      this.loading = false;
    }
  }

  beforeDestroy() {
    this.$offs(this.ons);
  }

  protected onServiceLoaded() {
    this.loading = false;
    if (this.service) {
      this.form = {
        name: { ...this.service.name } || {},
        short_description: { ...this.service.short_description } || {},
        description: { ...this.service.description } || {},
        price: String(this.service.price || 0),
        sale_price: String(this.service.sale_price || 0),
        on_sale: !!this.service.on_sale,
        active: !!this.service.active,
        orderby: String(this.service.orderby || 0),
        mandatory: !!this.service.mandatory,
        minimum_locker_cell_size_id: this.service.minimum_locker_cell_size_id || null,
        turnaround_time: String(this.service.turnaround_time || 0),
        tickets: String(this.service.tickets || 1),
        service_tag_ids: this.service.tags || [],
      };
      this.existingLogoUrl = this.service.logo_image_url || '';
      this.existingSecondUrl = this.service.second_image_url || '';
    }
  }

  // Same pattern as ServiceProviderCreate.vue onLogoImageChange / onSecondImageChange
  public onLogoImageChange(newFile: File): Promise<any> {
    return getFileImage(newFile)
        .then((res) => {
          this.addressImageLogo = res;
          this.fileImageLogo = newFile;
        });
  }

  public onSecondImageChange(newFile: File): Promise<any> {
    return getFileImage(newFile)
        .then((res) => {
          this.addressImageSecond = res;
          this.fileImageSecond = newFile;
        });
  }

  // Same pattern as ServiceProviderCreate.vue uploadImage
  private uploadImage(spId: number, serviceId: number, fileToUpload: File, endpoint: IMAGE_ENDPOINTS): Promise<any> {
    return this.$store.dispatch('imageUploaderModule/uploadImage', {
      endpoint: 'serviceProviders/' + spId + '/services/' + serviceId + '/' + endpoint,
      file: fileToUpload,
      translationPrefix: 'services.services.errors',
    });
  }

  protected onSave() {
    this.saving = true;
    const tagIds = this.form.service_tag_ids.map((tag: IServiceTag | string) => {
      if (typeof tag === 'string') return tag;
      return tag.id;
    });

    const data: any = {
      ...this.form,
      price: this.form.price,
      sale_price: this.form.sale_price || '0',
      turnaround_time: parseInt(this.form.turnaround_time) || 0,
      tickets: parseInt(this.form.tickets) || 1,
      orderby: parseInt(this.form.orderby) || 0,
      service_tag_ids: tagIds,
    };

    if (this.isCreate) {
      this.createService({ spId: this.spId, service: data });
    } else {
      this.updateService({ spId: this.spId, serviceId: this.serviceId, service: data });
    }
  }

  // Same pattern as ServiceProviderCreate.vue onCreated
  protected onServiceSaved(data: any) {
    this.saving = false;
    const serviceId = data && data.id;

    if (serviceId) {
      const uploads: Promise<any>[] = [];

      if (this.fileImageLogo) {
        uploads.push(this.uploadImage(this.spId, serviceId, this.fileImageLogo, IMAGE_ENDPOINTS.logoImage));
      }
      if (this.fileImageSecond) {
        uploads.push(this.uploadImage(this.spId, serviceId, this.fileImageSecond, IMAGE_ENDPOINTS.secondImage));
      }

      if (uploads.length > 0) {
        Promise.all(uploads).then(() => {
          this.$uiBus.emit('success', this.$t('common.saved'));
          this.goBack();
        }, () => {
          // Even if image uploads fail, navigate back
          this.$uiBus.emit('success', this.$t('common.saved'));
          this.goBack();
        });
      } else {
        this.$uiBus.emit('success', this.$t('common.saved'));
        this.goBack();
      }
    } else {
      this.$uiBus.emit('success', this.$t('common.saved'));
      this.goBack();
    }
  }

  protected onSaveError() {
    this.saving = false;
    this.deleting = false;
  }

  protected onDelete() {
    this.deleting = true;
    this.deleteService({ spId: this.spId, serviceId: this.serviceId });
  }

  protected onServiceDeleted() {
    this.deleting = false;
    this.showDeleteDialog = false;
    this.goBack();
  }

  protected goBack() {
    this.$router.push({ name: 'serviceProvidersServices', params: { spId: String(this.spId) } });
  }

}

